/**
 * Light formatting for thread notes (what the composer toolbar writes):
 * **bold**, *italic*, [text](url), bare links, "- " bullet lines and
 * @[Name](member:ID) mentions. Pure — rendered by ThreadRichText.
 */

export type RichInline =
  | { type: "text"; value: string }
  | { type: "bold"; children: RichInline[] }
  | { type: "italic"; children: RichInline[] }
  | { type: "link"; href: string; label: string }
  | { type: "mention"; name: string; id: string };

export type RichBlock =
  | { type: "paragraph"; lines: RichInline[][] }
  | { type: "list"; items: RichInline[][] };

const TOKEN =
  /@\[([^\]]+)\]\((?:member|person):(\d+)\)|\*\*(.+?)\*\*|\*([^*\s][^*]*?)\*|\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<]+[^\s<.,;:!?)\]])/g;

export const parseRichInline = (text: string): RichInline[] => {
  const out: RichInline[] = [];
  let last = 0;
  for (const match of text.matchAll(TOKEN)) {
    const index = match.index ?? 0;
    if (index > last)
      out.push({ type: "text", value: text.slice(last, index) });
    if (match[1] != null) {
      out.push({ type: "mention", name: match[1], id: match[2] });
    } else if (match[3] != null) {
      out.push({ type: "bold", children: parseRichInline(match[3]) });
    } else if (match[4] != null) {
      out.push({ type: "italic", children: parseRichInline(match[4]) });
    } else if (match[5] != null) {
      out.push({ type: "link", label: match[5], href: match[6] });
    } else if (match[7] != null) {
      out.push({ type: "link", label: match[7], href: match[7] });
    }
    last = index + match[0].length;
  }
  if (last < text.length) out.push({ type: "text", value: text.slice(last) });
  return out;
};

const BULLET = /^\s*[-*•]\s+/;

export const parseRichText = (text?: string | null): RichBlock[] => {
  const blocks: RichBlock[] = [];
  for (const line of (text ?? "").replace(/\r\n/g, "\n").split("\n")) {
    const last = blocks.at(-1);
    if (BULLET.test(line)) {
      const item = parseRichInline(line.replace(BULLET, ""));
      if (last?.type === "list") last.items.push(item);
      else blocks.push({ type: "list", items: [item] });
      continue;
    }
    if (!line.trim()) {
      // Blank line closes the current block.
      if (last && !(last.type === "paragraph" && last.lines.length === 0)) {
        blocks.push({ type: "paragraph", lines: [] });
      }
      continue;
    }
    const inline = parseRichInline(line);
    if (last?.type === "paragraph") last.lines.push(inline);
    else blocks.push({ type: "paragraph", lines: [inline] });
  }
  return blocks.filter(
    (block) => block.type === "list" || block.lines.length > 0,
  );
};
